// ─────────────────────────────────────────────────────────────────────────────
// NavDrawer — left-hand slide-out navigation for /SocialPopAdmin.
// Holds every page selection that previously lived in the header, styled in
// the portal's sleek black & white glass language. Closes on backdrop click,
// Escape, or after selecting a page.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect } from 'react';
import { X, LogOut, ChevronRight, Instagram } from 'lucide-react';
import { MTN_BRAND } from '@/lib/mtnshop';

export interface DrawerNavItem {
  id: string;
  label: string;
  desc: string;
  icon: React.ReactNode;
}

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  items: DrawerNavItem[];
  active: string;
  onSelect: (id: string) => void;
  onLogout: () => void;
}

export default function NavDrawer({ open, onClose, items, active, onSelect, onLogout }: NavDrawerProps) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <div className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* Panel */}
      <aside
        className={`absolute left-0 top-0 h-full w-[300px] max-w-[85vw] flex flex-col
          border-r border-white/10 bg-zinc-950/95 backdrop-blur-2xl
          shadow-[8px_0_40px_rgba(0,0,0,0.6)]
          transition-transform duration-300 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog"
        aria-label="Site navigation"
      >
        {/* Drawer header */}
        <div className="flex items-center gap-3 border-b border-white/[0.07] px-5 py-4">
          <img src={MTN_BRAND.avatar} alt="MTN" className="h-10 w-10 rounded-xl object-cover" />
          <div className="leading-tight flex-1 min-w-0">
            <p className="text-sm font-semibold text-white tracking-tight truncate">The MTN Shop</p>
            <p className="text-[11px] text-zinc-500">AI Social Media Hub</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 transition hover:bg-white/[0.08] hover:text-white"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Page selections */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-600">Pages</p>
          <div className="space-y-1">
            {items.map(n => {
              const isActive = active === n.id;
              return (
                <button
                  key={n.id}
                  onClick={() => { onSelect(n.id); onClose(); }}
                  className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition
                    ${isActive ? 'bg-white text-black' : 'text-zinc-300 hover:bg-white/[0.07] hover:text-white'}`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition
                    ${isActive ? 'border-black/10 bg-black/[0.06] text-black' : 'border-white/10 bg-white/[0.05] text-zinc-400 group-hover:text-white'}`}>
                    {n.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-medium leading-tight ${isActive ? 'text-black' : ''}`}>{n.label}</span>
                    <span className={`block text-[11px] leading-tight mt-0.5 ${isActive ? 'text-zinc-600' : 'text-zinc-500'}`}>{n.desc}</span>
                  </span>
                  <ChevronRight className={`h-4 w-4 shrink-0 transition ${isActive ? 'text-zinc-500' : 'text-zinc-700 group-hover:text-zinc-400'}`} />
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer actions */}
        <div className="border-t border-white/[0.07] p-3 space-y-1">
          <a
            href={MTN_BRAND.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-zinc-400 transition hover:bg-white/[0.07] hover:text-white"
          >
            <Instagram className="h-4 w-4" /> @{MTN_BRAND.handle}
          </a>
          <button
            onClick={() => { onClose(); onLogout(); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-zinc-400 transition hover:bg-white/[0.07] hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
    </div>
  );
}
